import Uppy, { UppyFile } from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import XHR from '@uppy/xhr-upload';

import NgConfig from '../NgConfig';
import { UppyOptionsWithOptionalRestrictions } from '@uppy/core/lib/Uppy';

type UploaderOptions = {
    csrfToken: string
    allowedFileTypes: string
    singleFile: boolean
    can_upload_multiple_files: boolean
    refname: string
}

enum UploaderEvent {
    uploadComplete = 'uploadComplete',
    uploadFailed = 'uploadFailed'
}

type Meta = any;

class Uploader {
    csrfToken: string
    uppy: Uppy<Meta, any> | null
    options: UploaderOptions
    allowedTypes: string = "*/*"
    singleFile: boolean = false
    eventNames: string[] = ['uploadComplete', 'uploadFailed']
    can_upload_multiple_files: boolean = false

    constructor(options: UploaderOptions) {
        this.options = options
        this.csrfToken = options.csrfToken
        this.allowedTypes = options.allowedFileTypes
        this.uppy = null
        this.singleFile = options.singleFile

        const allowedFileTypes = this.allowedTypes.split(",")

        const uppyOptions: UppyOptionsWithOptionalRestrictions<any, any> = {
            autoProceed: true,
            allowMultipleUploadBatches: false,
            restrictions: {
                allowedFileTypes: allowedFileTypes,
                maxFileSize: NgConfig.MAX_UPLOAD_FILE_SIZE,
                maxNumberOfFiles: NgConfig.MAX_NUMBER_OF_BATCH_UPLOADS
            },
            onBeforeUpload: (files: { [key: string]: UppyFile<Meta, any> }) => {
                if (this.options.can_upload_multiple_files) {
                    return true
                }
                if (Object.keys(files).length > 1) {
                    eventRecorder.captureEvent('limit_hit', { limit: 'multiple_files_free_user', files_count: Object.keys(files).length })
                    alert("As a free user, you can only upload one file at a time.")
                    return false
                }
                return true
            }

        }
        if (this.singleFile) {
            uppyOptions.restrictions && (uppyOptions.restrictions.maxNumberOfFiles = 1)
        }
        this.uppy = new Uppy<Meta, any>(uppyOptions)
            // @ts-ignore
            .use(Dashboard, {
                id: "tool",
                inline: true,
                target: '#uppy-tool-dashboard',
                showProgressDetails: true,
                width: 384, height: 'auto',
                singleFileFullScreen: true,
                disableInformer: true
            })
            // @ts-ignore
            .use(XHR, { endpoint: NgConfig.UPLOAD_URL, headers: { 'X-CSRFToken': this.csrfToken, mode: 'same-origin' } })
            .on('file-added', (file: any) => {
                this.uppy?.setFileMeta(file.id, { filename: file.name, content_type: file.type })
            })
            .on('upload-success', (file: any, response: any) => {
                console.debug("Upload success: ", file, response)
                eventRecorder.captureEvent('upload_success', { file: file, response: response })
                this.uppy?.setFileMeta(file.id, { ng_id: response.body.id })
                this.uppy?.setFileMeta(file.id, { ng_short_id: response.body.short_id })
            })
            .on('complete', async (result: any) => {
                console.log("Upload complete: ", result.successful, result.failed)
                // Check for out of space errors and redirect
                for (const f of result.failed) {
                    eventRecorder.captureEvent('upload_failed', { error: f.response.status })
                    if (f.response.status === 413) {
                        window.location.href = `/plans?tab=files-tab&upgrade_space=true`
                        return
                    }
                }
                if (result.failed.length > 0) {
                    let msg = "Upload failed: " + result.failed[0].error
                    if (result.successful.length > 0) {
                        msg += "\n Still proceeding with the successfully uploaded files."
                    }
                    alert(msg)
                }
                const short_ids = result.successful.map((f: any) => f.meta.ng_short_id)
                eventRecorder.captureEvent('upload_complete');
                if (short_ids.length > 0) {
                    window.location.href = `/tools/${this.options.refname}/params?short_ids=${short_ids.join(',')}`
                }
            })
            .on('error', (error: any) => {
                eventRecorder.captureEvent('upload_error', { error: error.detail.error });
                alert("Upload error: " + error.detail.error)
            })
            .on('restriction-failed', (file: any, error: any) => {
                console.error("Restriction failed: ", file, error)
                const msg: string = error?.message?.toLowerCase() ?? ''
                const limitType = msg.includes('size') ? 'max_file_size'
                    : msg.includes('you can only upload') ? 'max_number_of_files'
                    : msg.includes('type') || msg.includes('only upload:') ? 'file_type'
                    : 'unknown'
                eventRecorder.captureEvent('limit_hit', {
                    limit: limitType,
                    file_name: file?.name,
                    file_size: file?.size,
                    error_message: error?.message,
                })
                alert(`${file.name}: ${error}`)
            });
    }

    alertOnMultipleFiles(files: { [key: string]: UppyFile<Meta, any> }) {
        if (this.options.can_upload_multiple_files) {
            return true
        }
        if (Object.keys(files).length > 1) {
            this.uppy?.info("As a free user, you can only upload one file at a time.")
            return false
        }
        return true
    }

    _dispatchEvent(eventName: string, detail: any) {
        document.dispatchEvent(new CustomEvent(eventName, { detail }))
    }

    on(eventName: string, callback: (event: Event) => void) {
        document.addEventListener(eventName, callback)
    }

    off(eventName: string, callback: (event: Event) => void) {
        document.removeEventListener(eventName, callback)
    }

    off_all_events() {
        this.eventNames.forEach(eventName => {
            document.removeEventListener(eventName, () => { })
        })
    }


}
// Inputs here are delivered via global vars
// @ts-ignore
const uploader = new Uploader({ csrfToken: csrf, allowedFileTypes: allowedFileTypes, singleFile: singleFile, refname: refname, can_upload_multiple_files: can_upload_multiple_files ? true : false })
uploader.on(UploaderEvent.uploadFailed, (e: any) => {
    eventRecorder.captureEvent('upload_failed', { error: e.detail.error });
    alert("Upload failed: " + e.detail.error)
})